import { Suspense, useEffect, useState } from 'react';
import { lt, useFeatureFlag, useLoggerService } from '@mews/framework';
import { DialogFormProps, useDialogForm } from '@mews-commander/form';
import { OptimusProvider, OverlayedLoader } from '@mews-commander/ui';
import { WithAccountId } from '@mews-commander/types';
import { PaymentRequestType, SimpleAmount, useRepositoryQuery } from '@mews-commander/data-access';
import { Button, Disableable } from '@optimus-web/core';
import { FeatureFlag } from '@mews-commander/enums';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { ProcessPaymentDialogContent } from './ProcessPaymentDialogContent';
import { ProcessPaymentType } from './ProcessPaymentType';
import { DefaultSelectedFormValues, ReadOnlyFormFields } from './useDefaultFormValues';
import { DEFAULT_PROCESS_PAYMENT_TYPES, DISABLED_PROCESS_PAYMENT_TYPES } from './const';
import { getPaymentTypeDataTestId, getPaymentTypesEnabledSetting } from './utils';

export interface UseProcessPaymentDialogProps
    extends Omit<DialogFormProps, 'active' | 'renderForm' | 'id' | 'submitButtonDataTestId'>,
        WithAccountId {
    onFormSuccess?: () => void;
    billId?: string;
    allowedPaymentTypes?: ProcessPaymentType[];
    canLinkInvoices?: boolean;
    defaultSelectedFormValues?: DefaultSelectedFormValues;
    readOnlyFormFields?: ReadOnlyFormFields;
    paymentMethodCardsOptions?: ProcessPaymentDialogPaymentMethodCardsOptions;
    hideSinglePaymentMethodTitle?: boolean;
    allowedPaymentRequestTypeOptions?: PaymentRequestType[];
    billAmount?: SimpleAmount;
}

export interface ProcessPaymentDialogPaymentMethodCardsOptions {
    onlyCardsForMultipleMethods?: boolean;
    onlyDescriptionForSingleMethod: boolean;
}

const DIALOG_START_LOADING_MARK = 'process-payment-dialog-start-loading-mark';
const DIALOG_END_LOADING_MARK = 'process-payment-dialog-end-loading-mark';
const DIALOG_LOADING_TIME = 'process-payment-dialog-loading-time';

const SuspenseFallbackWithLoadingTimeLogger = ({
    allowedPaymentTypes = [],
    paymentType,
    accountId,
}: {
    paymentType: ProcessPaymentType | undefined;
    allowedPaymentTypes: ProcessPaymentType[];
} & WithAccountId) => {
    const enterprise = useCurrentEnterprise();
    const enterpriseId = enterprise?.id;
    const chainId = enterprise?.chainId;
    const loggerService = useLoggerService();
    const isProcessPaymentDialogLoadingLoggingEnabled = useFeatureFlag(
        FeatureFlag.processPaymentDialogLoadingLoggingEnabled
    );

    const containsGatewayCardOrTerminal =
        allowedPaymentTypes.includes(ProcessPaymentType.GatewayPayment) ||
        allowedPaymentTypes.includes(ProcessPaymentType.MewsTerminalPayment);
    const shouldLogLoadingTime = containsGatewayCardOrTerminal && isProcessPaymentDialogLoadingLoggingEnabled;
    const isInitialLoading = paymentType === undefined;

    useEffect(() => {
        if (!shouldLogLoadingTime || !enterpriseId || !chainId) {
            return;
        }
        window.performance.mark(DIALOG_START_LOADING_MARK);

        return () => {
            window.performance.mark(DIALOG_END_LOADING_MARK);
            const { duration: loadingTime } = window.performance.measure(
                DIALOG_LOADING_TIME,
                DIALOG_START_LOADING_MARK,
                DIALOG_END_LOADING_MARK
            );

            loggerService.log(
                'process-payment-dialog-loading',
                `Process payment dialog was loaded in ${loadingTime}ms`,
                {
                    loadingTime,
                    enterpriseId,
                    chainId,
                    isInitialLoading,
                    'mews.accountId': accountId,
                }
            );

            window.performance.clearMarks(DIALOG_START_LOADING_MARK);
            window.performance.clearMarks(DIALOG_END_LOADING_MARK);
            window.performance.clearMeasures(DIALOG_LOADING_TIME);
        };
        // For correct loading time measuring, it needs to run the hook only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <OverlayedLoader show />;
};

export const useProcessPaymentDialog = ({
    accountId,
    onFormSuccess,
    allowedPaymentTypes = DEFAULT_PROCESS_PAYMENT_TYPES,
    title = lt('ProcessPaymentPanelTitleForOptions'),
    ...props
}: UseProcessPaymentDialogProps) => {
    const [paymentType, setPaymentType] = useState<ProcessPaymentType>();
    const [renderDialog, toggleDialog] = useDialogForm({
        title,
        ...props,
        renderForm: ({ formRef, onSuccess, onSubmitting, onSubmitted }) => (
            <Suspense
                fallback={
                    <SuspenseFallbackWithLoadingTimeLogger
                        accountId={accountId}
                        paymentType={paymentType}
                        allowedPaymentTypes={allowedPaymentTypes}
                    />
                }
            >
                <ProcessPaymentDialogContent
                    {...props}
                    allowedPaymentTypes={allowedPaymentTypes}
                    currentFormRef={formRef}
                    accountId={accountId}
                    onFormSuccess={() => {
                        onFormSuccess?.();
                        onSuccess?.();
                    }}
                    onFormSubmitting={() => {
                        onSubmitting?.();
                    }}
                    onFormSubmitted={() => {
                        onSubmitted?.();
                    }}
                    onChangePaymentType={setPaymentType}
                />
            </Suspense>
        ),
        renderPrimaryAction: ({ handleSubmit, disabled }) => (
            <ProcessPaymentDialogSubmitButton
                handleSubmit={handleSubmit}
                disabled={disabled}
                paymentType={paymentType}
            />
        ),
    });

    const renderProcessPaymentDialog = () => <OptimusProvider density="spacious">{renderDialog()}</OptimusProvider>;
    return [renderProcessPaymentDialog, toggleDialog] as const;
};

interface ProcessPaymentDialogSubmitButtonProps extends Required<Disableable> {
    paymentType: ProcessPaymentType | undefined;
    handleSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const ProcessPaymentDialogSubmitButton = ({
    handleSubmit,
    disabled,
    paymentType,
}: ProcessPaymentDialogSubmitButtonProps) => {
    const { data, isFetching } = useRepositoryQuery('paymentMethods', 'getAvailability', {});
    const paymentTypesEnabledSetting = getPaymentTypesEnabledSetting(data);
    const isPciProxyMaintenanceDisabledPaymentsEnabled = useFeatureFlag(
        FeatureFlag.pciProxyMaintenanceDisabledPaymentsEnabled
    );
    const disabledPaymentType = paymentType
        ? !paymentTypesEnabledSetting[paymentType] ||
          (isPciProxyMaintenanceDisabledPaymentsEnabled && DISABLED_PROCESS_PAYMENT_TYPES.includes(paymentType))
        : true;

    return (
        <Button
            variant="primary"
            onClick={handleSubmit}
            data-test-id={getPaymentTypeDataTestId(paymentType)}
            disabled={disabledPaymentType || disabled}
            loading={isFetching}
            type="submit"
        >
            {lt('Submit')}
        </Button>
    );
};
